import { useState, useEffect, useMemo, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, Lock, Unlock, XCircle, Plus, RefreshCw, User, Phone, Clock, CheckCircle2, Hourglass, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || "Une erreur est survenue.");
const hm = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }).replace(":", "h");
const dayKey = (iso) => new Date(iso).toLocaleDateString("fr-CA", { timeZone: "Europe/Paris" });
const dayLabel = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Paris" });

const STATUS = {
  AVAILABLE: { label: "Disponible", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  HELD: { label: "En attente (patient)", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Hourglass },
  RESERVED: { label: "Réservé", cls: "bg-rose-100 text-rose-800 border-rose-300", icon: User },
  BLOCKED: { label: "Bloqué", cls: "bg-slate-200 text-slate-700 border-slate-300", icon: Lock },
};

export default function StaffCalendar() {
  const confirm = useConfirm();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ date: "", time: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/staff/cabin-calendar");
      setSlots(data.slots || []);
    } catch (e) { err(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const act = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { err(e); } finally { setBusy(false); } };
  const block = (id) => act(async () => { await api.post(`/staff/slots/${id}/block`); toast.success("Créneau bloqué."); });
  const unblock = (id) => act(async () => { await api.post(`/staff/slots/${id}/unblock`); toast.success("Créneau rouvert."); });
  const cancel = (id) => act(async () => { await api.post(`/staff/slots/${id}/cancel`); toast.success("Réservation annulée, créneau à nouveau disponible."); });
  const addSlot = () => {
    if (!form.date || !form.time) { toast.error("Indiquez une date et une heure."); return; }
    act(async () => { await api.post("/staff/slots", { date: form.date, time: form.time, durationMinutes: 30 }); setForm({ date: "", time: "" }); toast.success("Créneau ajouté."); });
  };
  const purgePast = async () => {
    if (!(await confirm({ title: "Supprimer les anciens", description: "Supprimer définitivement tous les créneaux et RDV passés ? Cette action est irréversible.", confirmText: "Supprimer", destructive: true }))) return;
    act(async () => { const { data } = await api.post("/staff/slots/purge-past"); toast.success(`${data.deleted} ancien(s) créneau(x) supprimé(s).`); });
  };

  const byDay = useMemo(() => {
    const g = {};
    slots.forEach((s) => { (g[dayKey(s.startAt)] ||= []).push(s); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  const counts = useMemo(() => {
    const c = { AVAILABLE: 0, HELD: 0, RESERVED: 0, BLOCKED: 0 };
    slots.forEach((s) => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [slots]);

  return (
    <div className="min-h-screen bg-background">
      <StaffHeader />
      <main className="mx-auto max-w-5xl px-4 py-8">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground"><CalendarDays className="h-5 w-5" /></div>
            <div><h1 className="font-heading text-2xl font-bold leading-tight">Calendrier des cabines</h1>
              <p className="text-[11px] text-muted-foreground">Réservation automatique · gestion manuelle de la disponibilité</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="cal-purge" variant="outline" onClick={purgePast} disabled={busy} className="h-10 rounded-xl gap-2 text-rose-600 border-rose-200 hover:bg-rose-50 dark:border-rose-900"><Trash2 className="h-4 w-4" /> Supprimer les anciens</Button>
            <Button data-testid="cal-refresh" variant="outline" onClick={load} disabled={busy} className="h-10 rounded-xl gap-2"><RefreshCw className="h-4 w-4" /> Actualiser</Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 my-4" data-testid="cal-counts">
          {Object.entries(STATUS).map(([k, v]) => (
            <span key={k} className={`text-xs font-medium rounded-full border px-3 py-1 ${v.cls}`}>{v.label} : {counts[k] || 0}</span>
          ))}
        </div>

        <Card className="rounded-2xl p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="cal-add">
          <div className="space-y-1"><Label className="text-xs">Ajouter un créneau — Date</Label>
            <Input data-testid="cal-add-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11 rounded-xl w-44" /></div>
          <div className="space-y-1"><Label className="text-xs">Heure</Label>
            <Input data-testid="cal-add-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="h-11 rounded-xl w-32" /></div>
          <Button data-testid="cal-add-btn" onClick={addSlot} disabled={busy} className="h-11 rounded-xl gap-2"><Plus className="h-4 w-4" /> Ajouter</Button>
        </Card>

        {loading ? <p className="text-muted-foreground">Chargement…</p> : byDay.length === 0 ? (
          <p className="text-muted-foreground">Aucun créneau à venir.</p>
        ) : (
          <div className="space-y-6" data-testid="cal-list">
            {byDay.map(([day, arr]) => (
              <div key={day}>
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">{dayLabel(arr[0].startAt)}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {arr.map((s) => {
                    const st = STATUS[s.status] || STATUS.AVAILABLE;
                    const Icon = st.icon;
                    return (
                      <Card key={s.id} data-testid={`cal-slot-${s.id}`} className="rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-heading font-bold text-lg flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /> {hm(s.startAt)}</span>
                          <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 flex items-center gap-1 ${st.cls}`}><Icon className="h-3 w-3" /> {st.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">{s.cabinName}</p>
                        {(s.status === "RESERVED" || s.status === "HELD") && (s.patientFirstName || s.patientLastName) && (
                          <div className="rounded-lg bg-muted/50 p-2 mb-3 text-xs space-y-0.5" data-testid={`cal-patient-${s.id}`}>
                            {s.patientFirstName && <p className="flex items-center gap-1.5"><User className="h-3 w-3" /> {s.patientFirstName} {s.patientLastName || ""}</p>}
                          </div>
                        )}
                        <div className="flex gap-2">
                          {s.status === "AVAILABLE" && <Button data-testid={`cal-block-${s.id}`} variant="outline" onClick={() => block(s.id)} disabled={busy} className="h-9 rounded-lg gap-1.5 text-xs flex-1"><Lock className="h-3.5 w-3.5" /> Bloquer</Button>}
                          {s.status === "BLOCKED" && <Button data-testid={`cal-unblock-${s.id}`} variant="outline" onClick={() => unblock(s.id)} disabled={busy} className="h-9 rounded-lg gap-1.5 text-xs flex-1"><Unlock className="h-3.5 w-3.5" /> Débloquer</Button>}
                          {(s.status === "RESERVED" || s.status === "HELD") && <Button data-testid={`cal-cancel-${s.id}`} variant="outline" onClick={() => cancel(s.id)} disabled={busy} className="h-9 rounded-lg gap-1.5 text-xs flex-1 text-rose-600 border-rose-200 hover:bg-rose-50"><XCircle className="h-3.5 w-3.5" /> Annuler (désistement)</Button>}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
